import { common, InvocationTransactionModel, } from '@neo-one/client-common';
import { AsyncIterableX } from '@reactivex/ix-esnext-esm/asynciterable/asynciterablex';
import BigNumber from 'bignumber.js';
import debug from 'debug';
import { AsyncBlockIterator } from '../AsyncBlockIterator';
import { clientUtils } from '../clientUtils';
import { NeoNotImplementedError } from '../errors';
import { convertAsset, convertBlock, convertContract, convertTransaction } from './convert';
import { JSONRPCClient } from './JSONRPCClient';
import { JSONRPCHTTPProvider } from './JSONRPCHTTPProvider';
const logger = debug('NEOONE:DataProvider');
export class DataProviderBase {
    constructor({ network, rpcURL, iterBlocksFetchTimeoutMS, iterBlocksBatchSize }) {
        this.network = network;
        this.mutableClient = new JSONRPCClient(typeof rpcURL === 'string' ? new JSONRPCHTTPProvider(rpcURL) : rpcURL);
        this.iterBlocksFetchTimeoutMS = iterBlocksFetchTimeoutMS;
        this.iterBlocksBatchSize = iterBlocksBatchSize;
    }
    setRPCURL(rpcURL) {
        this.mutableClient = new JSONRPCClient(new JSONRPCHTTPProvider(rpcURL));
    }
    async getUnclaimed(address) {
        return this.capture(async () => this.executeGetUnclaimed(address), 'neo_get_unclaimed');
    }
    async getClaimAmount(_input) {
        throw new NeoNotImplementedError('getClaimAmount');
    }
    async getUnspentOutputs(address) {
        return this.capture(async () => this.executeGetUnspentOutputs(address), 'neo_get_unspent');
    }
    async relayTransaction(transaction, networkFee) {
        return this.executeRelayTransaction(transaction, networkFee);
    }
    async getTransactionReceipt(hash, options) {
        return this.executeGetTransactionReceipt(hash, options);
    }
    async getInvocationData(_hash) {
        throw new NeoNotImplementedError('getInvocationData');
    }
    async testInvoke(transaction) {
        return this.executeTestInvoke(transaction);
    }
    async getAccount(address) {
        const account = await this.getAccountInternal(address);
        return {
            address,
            balances: account.balances.reduce((acc, { asset, value }) => ({
                ...acc,
                [asset]: new BigNumber(value),
            }), {}),
        };
    }
    async getAsset(hash) {
        const asset = await this.mutableClient.getAsset(hash);
        return convertAsset(asset);
    }
    async getBlock(hashOrIndex, options) {
        const block = await this.mutableClient.getBlock(hashOrIndex, options);
        return convertBlock(block, this.convertConfirmedTransaction);
    }
    iterBlocks(options = {}) {
        return AsyncIterableX.from(new AsyncBlockIterator({
            client: this,
            options,
            fetchTimeoutMS: this.iterBlocksFetchTimeoutMS,
            batchSize: this.iterBlocksBatchSize,
        }));
    }
    async getBestBlockHash() {
        return this.mutableClient.getBestBlockHash();
    }
    async getBlockCount() {
        return this.mutableClient.getBlockCount();
    }
    async getContract(address) {
        const contract = await this.mutableClient.getContract(address);
        return convertContract(contract);
    }
    async getMemPool() {
        return this.mutableClient.getMemPool();
    }
    async getTransaction(hash) {
        const transaction = await this.mutableClient.getTransaction(hash);
        return convertTransaction(transaction);
    }
    async getOutput(input) {
        return this.executeGetOutput(input);
    }
    async getConnectedPeers() {
        return this.mutableClient.getConnectedPeers();
    }
    async getNetworkSettings() {
        throw new NeoNotImplementedError('getNetworkSettings');
    }
    iterActionsRaw(_options = {}) {
        throw new NeoNotImplementedError('iterActionsRaw');
    }
    async call(contract, method, params) {
        const testTransaction = new InvocationTransactionModel({
            version: 1,
            gas: common.TEN_THOUSAND_FIXED8,
            script: clientUtils.getInvokeMethodScript({
                address: contract,
                method,
                params,
            }),
        });
        return this.testInvoke(testTransaction);
    }
    iterStorage(_address) {
        throw new NeoNotImplementedError('iterStorage');
    }
    async getAccountInternal(address) {
        return this.executeGetAccountInternal(address);
    }
    async capture(func, title) {
        try {
            const result = await func();
            logger('%o', { level: 'debug', title });
            return result;
        }
        catch (error) {
            logger('%o', { level: 'error', title, error: error.message });
            throw error;
        }
    }
}

//# sourceMappingURL=DataProviderBase.js.map
